/*
 * sb-configurator.js — Shimmerballs "Design your own" configurator.
 *
 * Build brief: docs/shimmerballs-configurator-claude-code-brief.md
 * Copy spec:   docs/shimmerballs-design-your-own-page.md
 * Preview:     assets/sb-3d.js (window.SB3D — module, tagged before this one
 *              so it has already evaluated by the time this runs)
 *
 * Hard rules:
 * - Custom balls are NOT purchasable. This module specifies and prices a ball,
 *   then hands off to the order form (spec v3.1). There is no cart path.
 * - Every price comes from sb-pricing.js, computed in the displayed currency
 *   using that currency's own ladder. The goods total must match the
 *   salesperson's quotation exactly, so nothing here rounds or converts.
 * - Microcopy is verbatim from the brief.
 * - Vector preferred (PDF/AI/SVG); raster accepted with a vectorising notice.
 */

import { quote, formatMoney, CURRENCIES } from 'sb-pricing';
import { OrderForm } from 'sb-order-form';

/*
 * pdf.js, pinned.
 *
 * This runs on the page where customers hand us their artwork, so a compromised
 * or swapped CDN file would execute with access to that file and to the rest of
 * the storefront. The integrity hash makes the browser refuse anything whose
 * bytes do not match, and crossorigin="anonymous" is REQUIRED for the check to
 * happen at all — without it the response is opaque and integrity is silently
 * skipped, which looks exactly like it is working.
 *
 * sha384 of pdf.js 3.11.174 (320,004 bytes), computed from the file itself on
 * 1 Sep 2026. Changing the version means recomputing the hash:
 *   curl -sSL <url> | openssl dgst -sha384 -binary | openssl base64 -A
 */
const PDFJS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDFJS_SRI = 'sha384-/1qUCSGwTur9vjf/z9lmu/eCUYbpOTgSjmpbMQZ1/CtX2v/WcAIKqRv+U1DUCG6e';

/*
 * 20MB (Zac, 1 Sep). Chosen against the real ceiling rather than a guess: Zoho
 * was tested directly on this org and accepted attachments at 21MB and 26MB,
 * so 20 leaves headroom on the one limit that actually matters — an artwork
 * file the CRM refuses is a lead with no artwork attached to it.
 *
 * 5MB was the first choice and would have turned away a designer's layered AI
 * or a press-ready PDF, which routinely run 10-30MB. Those are exactly the
 * customers most likely to have print-ready files.
 */
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const VECTOR_EXTENSIONS = ['pdf', 'ai', 'svg'];

/*
 * Raster uploads, accepted deliberately — Zac's call, 27 Aug.
 *
 * The brief said vector only and refused everything else outright. That is
 * correct about print and wrong about customers: plenty of people have no idea
 * what a file type is, and a hard refusal loses the order rather than fixing
 * the artwork. These are accepted, and the customer is told plainly that the
 * design team will redraw it.
 *
 * Only PNG and JPEG. They cover essentially every logo a non-designer actually
 * has to hand, and both decode natively in an <img>, so no extra library.
 */
const RASTER_EXTENSIONS = ['png', 'jpg', 'jpeg'];

/*
 * The accepted-formats note is markup, not a string — see the section template.
 * Kept out of MSG for the same reason SPEC_BLOCKED_MSG is: MSG holds the
 * brief's verbatim microcopy, and this belongs to a policy the brief did not
 * have.
 */
const UNSUPPORTED_MSG =
  "We can't read that file type. Send a PDF, AI, SVG, PNG or JPEG.";
const EXPORT_SIZE = 1600;
/*
 * The spec sheet embeds two images as data URIs in a printable document, so
 * this trades resolution against document weight: 1600px twice puts the sheet
 * past 2MB, which is slow to open and unpleasant to email. 1100px is still
 * ~140dpi at the printed size on A4.
 */
const SPEC_SHEET_SIZE = 1100;

/*
 * Kept out of MSG deliberately. MSG holds the brief's verbatim microcopy; this
 * is a new string for a surface the brief does not cover, and folding it in
 * would blur that line.
 */
const SPEC_BLOCKED_MSG =
  'Your browser blocked the technical sheet window. Allow pop-ups for this site and try again.';

const MSG = {
  tooLarge: 'That file is over 20MB. Flatten or resave it and try again, or email it to us and we\u2019ll take it from there.',
  noPreview: 'No preview for this file type. Drag to position your artwork.',
};

class SBConfigurator {
  constructor(root) {
    this.root = root;
    const dataEl = document.getElementById(`sb-cfg-data-${root.dataset.sectionId}`);
    if (!dataEl) return;
    this.data = JSON.parse(dataEl.textContent);

    // The 3D renderer owns this canvas now — do not acquire a 2D context on
    // it, a canvas only ever gets one context type.
    this.canvas = root.querySelector('#sb-cfg-canvas');

    this.state = {
      size: null,
      colour: null,
      sides: 'one',
      display: 'ground',
      fixingType: 'rings', // 'rings' | 'drings' — count is derived from size
      fixingPole: 'valve', // 'valve' | 'opposite' — D-rings only
      twoTone: false, // alternate the panels between two colours
      colour2: null,
      qty: this.data.minQty,
      /*
       * One pump per order regardless of quantity — a checkbox, not a stepper
       * (Zac, 1 Sep). Spec 2.6 prices it as a single add-on.
       */
      pump: false,
      /*
       * Opens on whatever Shopify Markets resolved for this visitor when that
       * is one of the three we quote in, otherwise GBP. The selector overrides
       * it, and the choice travels into the form and the lead.
       */
      currency: CURRENCIES.includes(this.data.shopCurrency) ? this.data.shopCurrency : 'GBP',
      artwork: null, // { file, imageData, canvas, previewUnsupported }
      /*
       * A genuinely different design for the back, not a mirror of the front.
       * Only set when the buyer explicitly chooses "different design on the
       * back", because it is what triggers the second setup fee. Wired up in
       * the two-artwork step; null keeps designCount() at one until then.
       */
      backArtwork: null,
      // Artwork is locked to the centre of the equator (brief §10). Height
      // positioning is deferred, so these stay at zero rather than being
      // removed — the spec-text field keeps its shape for when it returns.
      lx: 0,
      ly: 0,
      sizePct: this.data.logoRatio,
      rotationDeg: 0,
      /*
       * 'same' repeats the front design on the back — still one setup fee.
       * 'different' is the declaration that buys the second one.
       */
      backMode: 'same',
      backSizePct: this.data.logoRatio,
      backRotationDeg: 0,
    };

    this.pdfjsPromise = null;

    this.el = {};
    for (const id of [
      'colour-name', 'upload-group', 'upload', 'upload-empty', 'upload-file', 'file',
      'upload-error', 'upload-ok', 'upload-vectorise', 'upload-nopreview',
      'remove', 'sliders', 'art-size', 'art-size-out',
      'art-rot', 'art-rot-out', 'qty', 'qty-minus', 'qty-plus', 'price-lines',
      'total', 'atc', 'atc-error', 'preview-hint', 'currency', 'pump',
      'pump-price', 'tier-note', 'enquire',
      'fixing-count', 'fixing-pole', 'view-fixings', 'spec',
      'twotone', 'twotone-panel', 'colour2-name',
      'back-panel', 'back-note', 'back-upload-wrap', 'back-upload',
      'back-upload-empty', 'back-upload-file', 'back-file', 'back-upload-error',
      'back-upload-ok', 'back-upload-vectorise', 'back-upload-nopreview',
      'back-remove', 'back-sliders', 'back-art-size', 'back-art-size-out',
      'back-art-rot', 'back-art-rot-out',
    ]) {
      this.el[id.replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = root.querySelector(`#sb-cfg-${id}`);
    }

    this.bind();
    this.readControls();
    this.syncBackPanel();
    this.updatePrice();
    this.initPreview();
    this.orderForm = new OrderForm(this);
  }

  /* ------------------------------------------------------------ 3D preview */

  async initPreview() {
    await window.SB3D.init(this.canvas, {
      envUrl: this.data.envUrl,
      colours: this.data.colours,
      // Built at the right size from the start. The setSize below is then a
      // no-op, which is the point: it must not animate on first paint.
      sizeCm: this.sizeCm(),
    });
    window.SB3D.setSize(this.sizeCm());
    this.state.colour2 =
      (this.root.querySelector('input[name="sb-colour-2"]:checked') || {}).value || null;
    this.pushColours();
    this.syncLogoMax();
    this.syncFixings();
  }

  /*
   * Printed artwork width in centimetres.
   *
   * sizePct is a percentage of DIAMETER, and the decal spans that fraction of
   * the diameter as ARC LENGTH across the skin — so the centimetre figure is a
   * measurement along the curve, which is what a printer works to, not a
   * straight-line chord.
   *
   * The percentage stays the underlying state: the renderer takes it and the
   * order spec reports it. Only what the customer reads changes.
   */
  artWidthCm() {
    return (this.state.sizePct / 100) * this.sizeCm();
  }

  /* One decimal, but never a bare ".0" — 28cm, 6.4cm, 150cm. */
  formatCm(cm) {
    const r = Math.round(cm * 10) / 10;
    return `${Number.isInteger(r) ? r : r.toFixed(1)}cm`;
  }

  /* Every place the printed width is shown, refreshed together. Size changes
   * move this too: the same percentage of a bigger ball is a bigger print. */
  syncArtSize() {
    // Just the measurement in the output — the row is labelled "Width", so
    // repeating the word both duplicates the label and wraps the 3.5rem column
    // onto two lines.
    if (this.el.artSizeOut) this.el.artSizeOut.textContent = this.formatCm(this.artWidthCm());
    if (this.el.uploadOk) {
      this.el.uploadOk.textContent = `Artwork prints ${this.formatCm(this.artWidthCm())} wide across the ball.`;
    }
    const backCm = (this.state.backSizePct / 100) * this.sizeCm();
    if (this.el.backArtSizeOut) this.el.backArtSizeOut.textContent = this.formatCm(backCm);
    if (this.el.backUploadOk) {
      this.el.backUploadOk.textContent = `Back design prints ${this.formatCm(backCm)} wide across the ball.`;
    }
  }

  /* Numeric centimetres from a size option value like "150cm". */
  sizeCm() {
    return parseInt(String(this.state.size || '').replace(/[^0-9]/g, ''), 10) || 40;
  }

  /*
   * Slider ceiling comes from the cap geometry, not a section setting — with
   * placement locked to the equator this is the whole safe-zone mechanism.
   */
  syncLogoMax() {
    const max = window.SB3D.maxLogoPct();
    if (!max) return;
    this.el.artSize.max = String(max);
    if (this.state.sizePct > max) {
      this.state.sizePct = max;
      this.el.artSize.value = String(max);
    }
    this.syncArtSize();
  }

  /* ------------------------------------------------------------ data */

  variantFor(size, colour) {
    return this.data.product.variants.find((v) => v.size === size && v.colour === colour) || null;
  }

  formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    const format = this.data.moneyFormat || '£{{ amount }}';
    return format
      .replace(/\{\{\s*amount\s*\}\}/, Number(value).toLocaleString('en-GB', { minimumFractionDigits: 2 }))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toLocaleString('en-GB'))
      .replace(/<[^>]+>/g, '');
  }

  /* ------------------------------------------------------------ controls */

  bind() {
    this.root.addEventListener('change', (e) => {
      const input = e.target;
      if (input.name === 'sb-size') {
        this.state.size = input.value;
        window.SB3D.setSize(this.sizeCm());
        this.syncLogoMax(); // also refreshes the printed width, which scales with the ball
        this.syncFixings(); // count steps at the 150cm boundary
        this.updatePrice();
      }
      if (input.name === 'sb-fixing-type') {
        this.state.fixingType = input.value;
        this.syncFixings();
      }
      if (input.name === 'sb-fixing-pole') {
        this.state.fixingPole = input.value;
        this.syncFixings();
      }
      if (input.name === 'sb-colour') {
        this.state.colour = input.value;
        this.el.colourName.textContent = input.dataset.colourName || input.value;
        this.pushColours();
        this.updatePrice();
      }
      if (input.name === 'sb-colour-2') {
        this.state.colour2 = input.value;
        this.pushColours();
      }
      if (input.name === 'sb-sides') {
        this.state.sides = input.value;
        this.syncBackPanel();
        this.pushArtwork();
        this.updatePrice();
      }
      if (input.name === 'sb-display') {
        this.state.display = input.value;
        this.syncFixings(); // display decides which pole carries the valve
        this.updatePrice();
      }
    });

    // Upload — same wiring for both slots, pointed at different elements.
    this.bindUpload('front');
    if (this.el.backUpload) this.bindUpload('back');

    // Which design goes on the back. This is what buys the second setup fee,
    // so it repaints the ball and reprices in one go.
    for (const input of this.root.querySelectorAll('input[name="sb-back-mode"]')) {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        this.state.backMode = input.value === 'different' ? 'different' : 'same';
        this.syncBackPanel();
        this.updatePrice();
        this.pushArtwork();
      });
    }

    if (this.el.backArtSize) {
      this.el.backArtSize.addEventListener('input', () => {
        this.state.backSizePct = parseInt(this.el.backArtSize.value, 10);
        this.syncArtSize();
        this.pushArtworkTransform();
      });
    }
    if (this.el.backArtRot) {
      this.el.backArtRot.addEventListener('input', () => {
        this.state.backRotationDeg = parseInt(this.el.backArtRot.value, 10);
        if (this.el.backArtRotOut) this.el.backArtRotOut.textContent = `${this.state.backRotationDeg}°`;
        this.pushArtworkTransform();
      });
    }

    // Sliders
    this.el.artSize.addEventListener('input', () => {
      this.state.sizePct = parseInt(this.el.artSize.value, 10);
      this.syncArtSize();
      this.pushArtworkTransform();
    });
    this.el.artRot.addEventListener('input', () => {
      this.state.rotationDeg = parseInt(this.el.artRot.value, 10);
      this.el.artRotOut.textContent = `${this.state.rotationDeg}°`;
      this.pushArtworkTransform();
    });

    // Quantity
    const clampQty = (n) => Math.max(this.data.minQty, Math.floor(n) || this.data.minQty);
    this.el.qtyMinus.addEventListener('click', () => {
      this.state.qty = clampQty(this.state.qty - 1);
      this.el.qty.value = this.state.qty;
      this.updatePrice();
    });
    this.el.qtyPlus.addEventListener('click', () => {
      this.state.qty = clampQty(this.state.qty + 1);
      this.el.qty.value = this.state.qty;
      this.updatePrice();
    });
    this.el.qty.addEventListener('change', () => {
      this.state.qty = clampQty(parseInt(this.el.qty.value, 10));
      this.el.qty.value = this.state.qty;
      this.updatePrice();
    });

    // Canvas pointer input belongs to the 3D orbit control now — artwork is
    // locked to the centre of the equator, so there is nothing to drag.

    // Actions
    if (this.el.spec) this.el.spec.addEventListener('click', () => this.openSpecSheet());
    this.el.atc.addEventListener('click', () => this.startOrder());

    if (this.el.enquire) {
      // Same form, artwork optional (spec 4).
      this.el.enquire.addEventListener('click', () => this.startOrder({ artworkOptional: true }));
    }

    if (this.el.currency) {
      this.el.currency.value = this.state.currency;
      this.el.currency.addEventListener('change', () => {
        this.state.currency = this.el.currency.value;
        this.updatePrice();
      });
    }

    if (this.el.pump) {
      this.el.pump.addEventListener('change', () => {
        this.state.pump = this.el.pump.checked;
        this.updatePrice();
      });
    }

    // Two-tone panels.
    this.el.twotone.addEventListener('click', () => {
      this.state.twoTone = !this.state.twoTone;
      this.el.twotone.setAttribute('aria-pressed', String(this.state.twoTone));
      this.el.twotone.textContent = this.state.twoTone ? 'Single colour' : 'Add a second colour';
      this.el.twotonePanel.hidden = !this.state.twoTone;
      this.pushColours();
    });

    // Preset views. Toggles between the default framing and the fixings angle.
    this.el.viewFixings.addEventListener('click', () => {
      this.viewingFixings = !this.viewingFixings;
      window.SB3D.setView(this.viewingFixings ? 'fixings' : 'default');
      this.el.viewFixings.classList.toggle('is-active', this.viewingFixings);
      this.el.viewFixings.textContent = this.viewingFixings ? 'Back to full view' : 'View fixings';
    });
  }

  /*
   * Hand both colours to the renderer. The second is only meaningful while
   * two-tone is on, so it is passed as null otherwise rather than being
   * remembered silently.
   */
  pushColours() {
    const second = this.state.twoTone ? this.state.colour2 : null;
    window.SB3D.setColour(this.state.colour, second);
    if (second) this.el.colour2Name.textContent = second;
  }

  /* Human-readable colour line, shared by the spec text and the cart line. */
  colourText() {
    return this.state.twoTone && this.state.colour2
      ? `${this.state.colour} / ${this.state.colour2} (alternating panels)`
      : this.state.colour;
  }

  /*
   * Push the fixing choice at the renderer and restate the derived count. The
   * count follows size and is never chosen; the pole choice only exists for
   * D-rings, so its control is hidden otherwise.
   */
  syncFixings() {
    window.SB3D.setFixings({
      type: this.state.fixingType,
      pole: this.state.fixingPole,
      display: this.state.display,
    });
    const drings = this.state.fixingType === 'drings';
    this.el.fixingPole.hidden = !drings;

    /*
     * Re-aim the preset if it is currently active. Size sets the framing
     * distance and display/pole decide which pole the hardware is on, so
     * without this the camera keeps staring at wherever the fixings used to be.
     */
    if (this.viewingFixings) window.SB3D.setView('fixings');

    const s = window.SB3D.fixingSummary ? window.SB3D.fixingSummary() : null;
    if (s) {
      const noun = drings ? (s.count === 1 ? 'D-ring' : 'D-rings') : 'rings';
      const where = drings && this.state.fixingPole === 'opposite' ? 'opposite the valve' : 'at the valve';
      this.el.fixingCount.textContent = `${s.count} ${noun}, ${where}. The count is set by size, and costs the same either way.`;
    }
  }

  /* Human-readable fixing line, shared by the spec text and the cart line. */
  fixingText() {
    const s = window.SB3D.fixingSummary ? window.SB3D.fixingSummary() : null;
    if (!s) return '';
    const drings = s.type === 'drings';
    const noun = drings ? (s.count === 1 ? 'D-ring' : 'D-rings') : 'ring tabs';
    const where = drings && s.pole === 'opposite' ? 'opposite pole' : 'valve pole';
    return `${s.count} × ${noun} (${where})`;
  }

  readControls() {
    const size = this.root.querySelector('input[name="sb-size"]:checked');
    const colour = this.root.querySelector('input[name="sb-colour"]:checked');
    if (size) this.state.size = size.value;
    if (colour) this.state.colour = colour.value;
  }

  /* Hand the current artwork bitmap to the renderer. */
  pushArtwork() {
    const art = this.state.artwork;
    window.SB3D.setArtwork(art && art.canvas ? art.canvas : null, {
      twoSided: this.state.sides === 'two',
      slot: 'front',
    });
    /*
     * The back bitmap is only handed over when the buyer has actually asked for
     * a different design. In 'same' mode the slot is cleared, which is what
     * makes the renderer fall back to repeating the front — leaving a stale
     * back bitmap in place would keep painting a design they had switched away
     * from.
     */
    const back = this.state.backMode === 'different' ? this.state.backArtwork : null;
    window.SB3D.setArtwork(back && back.canvas ? back.canvas : null, { slot: 'back' });
    this.pushArtworkTransform();
  }

  pushArtworkTransform() {
    window.SB3D.setArtworkTransform({
      sizePct: this.state.sizePct,
      rotationDeg: this.state.rotationDeg,
    }, { slot: 'front' });
    window.SB3D.setArtworkTransform({
      sizePct: this.state.backSizePct,
      rotationDeg: this.state.backRotationDeg,
    }, { slot: 'back' });
    const max = window.SB3D.maxLogoPct();
    this.el.previewHint.textContent =
      this.state.artwork && max && this.state.sizePct >= max
        ? "Artwork can't cross the top or bottom of the ball. Those areas deform in production, so keep it inside the guide."
        : 'Drag to rotate · scroll to zoom';
  }

  /* ------------------------------------------------------------ upload */

  /*
   * The two upload slots are the same machine pointed at different elements.
   * Resolving them through one map keeps handleFile/clearArtwork single
   * implementations — two near-copies would drift the moment one is fixed.
   */
  bindUpload(which) {
    const els = this.slotEls(which);
    const { upload, input } = els;
    upload.addEventListener('click', () => input.click());
    upload.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); }
    });
    upload.addEventListener('dragover', (e) => { e.preventDefault(); upload.classList.add('is-dragover'); });
    upload.addEventListener('dragleave', () => upload.classList.remove('is-dragover'));
    upload.addEventListener('drop', (e) => {
      e.preventDefault();
      upload.classList.remove('is-dragover');
      if (e.dataTransfer.files[0]) this.handleFile(e.dataTransfer.files[0], which);
    });
    input.addEventListener('change', () => {
      if (input.files[0]) this.handleFile(input.files[0], which);
    });
    els.remove.addEventListener('click', () => this.clearArtwork(which));
  }

  /*
   * The back controls only exist for two-sided jobs, and the second upload only
   * for jobs that declared a different design. Hiding rather than disabling:
   * a disabled upload box invites clicking at it.
   */
  syncBackPanel() {
    /*
     * No print hides the whole artwork step. Leaving an upload box on screen
     * for a ball that will not be printed is an invitation to attach a file
     * and assume it is going to appear on the product.
     */
    const noPrint = this.state.sides === 'none';
    if (this.el.uploadGroup) this.el.uploadGroup.hidden = noPrint;

    const twoSided = this.state.sides === 'two';
    if (this.el.backPanel) this.el.backPanel.hidden = !twoSided;
    const different = twoSided && this.state.backMode === 'different';
    if (this.el.backUploadWrap) this.el.backUploadWrap.hidden = !different;
    if (this.el.backNote) {
      this.el.backNote.textContent = different
        ? 'A second design means a second setup fee. It is charged once, not per ball.'
        : 'The same design is printed on both sides. One setup fee.';
    }
  }

  slotEls(which) {
    return which === 'back'
      ? {
          key: 'backArtwork',
          upload: this.el.backUpload, empty: this.el.backUploadEmpty,
          file: this.el.backUploadFile, input: this.el.backFile,
          error: this.el.backUploadError, ok: this.el.backUploadOk,
          vectorise: this.el.backUploadVectorise, nopreview: this.el.backUploadNopreview,
          remove: this.el.backRemove, sliders: this.el.backSliders,
        }
      : {
          key: 'artwork',
          upload: this.el.upload, empty: this.el.uploadEmpty,
          file: this.el.uploadFile, input: this.el.file,
          error: this.el.uploadError, ok: this.el.uploadOk,
          vectorise: this.el.uploadVectorise, nopreview: this.el.uploadNopreview,
          remove: this.el.remove, sliders: this.el.sliders,
        };
  }

  async handleFile(file, which = 'front') {
    const els = this.slotEls(which);
    els.error.hidden = true;
    if (els.vectorise) els.vectorise.hidden = true;
    if (els.nopreview) els.nopreview.hidden = true;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const isVector = VECTOR_EXTENSIONS.includes(ext);
    const isRaster = RASTER_EXTENSIONS.includes(ext);

    if (!isVector && !isRaster) {
      this.showUploadError(UNSUPPORTED_MSG, which);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      this.showUploadError(MSG.tooLarge, which);
      return;
    }

    let artCanvas = null;
    let previewUnsupported = false;

    try {
      if (ext === 'svg') {
        artCanvas = await this.rasteriseSvg(file);
      } else if (isRaster) {
        artCanvas = await this.decodeRaster(file);
      } else {
        // PDF, and AI (most .ai files are PDF-compatible — try, fall back gracefully).
        artCanvas = await this.rasterisePdf(file);
      }
    } catch (err) {
      if (ext === 'ai' || ext === 'pdf') {
        previewUnsupported = true;
      } else {
        this.showUploadError(UNSUPPORTED_MSG, which);
        return;
      }
    }

    this.state[els.key] = {
      file,
      canvas: artCanvas,
      imageData: artCanvas ? artCanvas.getContext('2d').getImageData(0, 0, artCanvas.width, artCanvas.height) : null,
      previewUnsupported,
      // Production needs to know this, and so does the spec sheet.
      needsVectorising: isRaster,
    };
    if (els.vectorise) els.vectorise.hidden = !isRaster;
    /*
     * MSG.noPreview has existed since the first build and was never shown:
     * previewUnsupported was set and then nothing was done with it, so a PDF
     * that failed to rasterise left the customer looking at a blank ball with
     * no explanation. The string was written for this exact case.
     */
    if (els.nopreview) {
      els.nopreview.textContent = previewUnsupported ? MSG.noPreview : '';
      els.nopreview.hidden = !previewUnsupported;
    }
    els.upload.classList.add('has-file');
    els.empty.hidden = true;
    els.file.hidden = false;
    els.file.textContent = file.name;
    els.ok.hidden = false;
    els.remove.hidden = false;
    els.sliders.hidden = false;
    this.syncArtSize();
    this.updatePrice();
    this.pushArtwork();
  }

  showUploadError(msg, which = 'front') {
    const els = this.slotEls(which);
    els.error.textContent = msg;
    els.error.hidden = false;
  }

  clearArtwork(which = 'front') {
    const els = this.slotEls(which);
    this.state[els.key] = null;
    els.input.value = '';
    els.upload.classList.remove('has-file');
    els.empty.hidden = false;
    els.file.hidden = true;
    els.ok.hidden = true;
    if (els.vectorise) els.vectorise.hidden = true;
    if (els.nopreview) els.nopreview.hidden = true;
    els.remove.hidden = true;
    els.sliders.hidden = true;
    els.error.hidden = true;
    this.updatePrice();
    this.pushArtwork();
  }

  /*
   * PNG and JPEG. Decoded straight through an <img>, then trimmed like the PDF
   * path so a logo sitting on a white background lands on the ball at the size
   * of its artwork rather than the size of its margins.
   *
   * imageToCanvas is called WITHOUT the vector flag on purpose: a raster source
   * has a real resolution and upscaling it would invent detail that is not
   * there, which is exactly the blur the design team is being asked to fix.
   */
  decodeRaster(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const img = new Image();
        img.onload = () => resolve(this.trimCanvas(this.imageToCanvas(img, false)));
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  }

  rasteriseSvg(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const img = new Image();
        img.onload = () => resolve(this.imageToCanvas(img, true));
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  }

  async rasterisePdf(file) {
    if (!this.pdfjsPromise) {
      this.pdfjsPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = PDFJS_SRC;
        s.integrity = PDFJS_SRI;
        // Required: without it the response is opaque and integrity is skipped.
        s.crossOrigin = 'anonymous';
        s.onload = resolve;
        /*
         * Also fires when the integrity check FAILS, which is the point — a
         * mismatched file is refused rather than executed, and the customer
         * gets the no-preview path instead of a silently swapped library.
         */
        s.onerror = () => reject(new Error('pdf.js failed to load or failed its integrity check'));
        document.head.appendChild(s);
      });
    }
    await this.pdfjsPromise;
    const url = URL.createObjectURL(file);
    try {
      const pdf = await window.pdfjsLib.getDocument(url).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 2 });
      const c = document.createElement('canvas');
      c.width = viewport.width;
      c.height = viewport.height;
      await page.render({ canvasContext: c.getContext('2d'), viewport }).promise;
      return this.trimCanvas(c);
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  /*
   * `vector` matters more than it looks. An SVG's intrinsic size comes from its
   * viewBox, which is arbitrary — a logo authored at 120x40 units rasterised at
   * 120px and was then magnified onto a 4096px equirect map, which is the whole
   * of the "SVG still looks blurry" report. Clamping scale at 1 is correct for
   * a raster upload, where upsampling invents nothing; for a vector source the
   * browser genuinely re-renders at whatever size it is asked for, so it should
   * be asked for a big one.
   */
  imageToCanvas(img, vector = false) {
    const maxDim = vector ? 2400 : 1200;
    const natural = Math.max(img.width, img.height) || maxDim;
    const scale = vector ? maxDim / natural : Math.min(1, maxDim / natural);
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(img.width * scale));
    c.height = Math.max(1, Math.round(img.height * scale));
    const g = c.getContext('2d');
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    g.drawImage(img, 0, 0, c.width, c.height);
    return c;
  }

  /** Trim uniform white/transparent margins so PDFs warp to their content box. */
  trimCanvas(c) {
    const ctx = c.getContext('2d');
    const { data, width, height } = ctx.getImageData(0, 0, c.width, c.height);
    let minX = width, minY = height, maxX = 0, maxY = 0;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        const opaque = data[i + 3] > 16;
        const white = data[i] > 246 && data[i + 1] > 246 && data[i + 2] > 246;
        if (opaque && !white) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX <= minX || maxY <= minY) return c;
    const pad = 4;
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(width - 1, maxX + pad); maxY = Math.min(height - 1, maxY + pad);
    const out = document.createElement('canvas');
    out.width = maxX - minX + 1;
    out.height = maxY - minY + 1;
    out.getContext('2d').drawImage(c, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
    return out;
  }

  /* ------------------------------------------------------------ preview */

  // The 2D preview (loadBallImage / render / drawHardware and the SBWarp
  // projection) is gone — assets/sb-3d.js owns the preview surface. Everything
  // that used to force a repaint now pushes state at window.SB3D instead.

  /* ------------------------------------------------------------ pricing */

  /*
   * How many distinct artworks the job carries, which is what the setup fee
   * multiplies by (spec 2.3). One design printed on both sides is still one
   * setup; two different designs front and back is two.
   */
  designCount() {
    /*
     * Keyed off the declared intent, not off whether a file has landed yet.
     * Someone who says "different design on the back" and sends the file by
     * email afterwards is still a two-design job, and charging only on upload
     * would let that order through at half the setup fee.
     */
    if (this.state.sides === 'none') return 0; // nothing to prepare, nothing to charge
    return this.state.sides === 'two' && this.state.backMode === 'different' ? 2 : 1;
  }

  currentQuote() {
    return quote({
      size: this.state.size,
      qty: this.state.qty,
      sides: this.state.sides,
      designs: this.designCount(),
      pump: this.state.pump,
      currency: this.state.currency,
    });
  }

  /*
   * Itemised, because the spec makes the goods total reproducible line by line
   * against the quotation. A single figure is not checkable against anything.
   *
   * The button is never disabled here. Under the old cart model an incomplete
   * design could not be bought, so blocking made sense; now the button opens an
   * order form, and someone with no artwork yet is exactly who the secondary
   * "not ready" path is for. Artwork is optional all the way through.
   */
  updatePrice() {
    this.el.atcError.hidden = true;

    const q = this.currentQuote();
    if (!q) {
      this.el.priceLines.innerHTML = '';
      this.el.total.textContent = '\u2026';
      return;
    }

    const money = (v) => formatMoney(v, q.currency);
    const each = money(q.unit.ball);
    const rows = [
      [
        `Ball: ${this.state.size} × ${q.qty}`,
        q.tier.discount
          ? `${each} each less ${Math.round(q.tier.discount * 100)}%`
          : `${each} each`,
        q.lines.ball,
      ],
    ];
    /*
     * An unprinted ball shows neither line rather than showing them at zero.
     * A "£0.00" row invites the reader to wonder what is broken; an absent row
     * says plainly that the charge does not apply.
     */
    if (q.printed) {
      rows.push([
        `Print: ${q.sidesMultiplier === 2 ? 'both sides' : 'one side'} × ${q.qty}`,
        `${money(q.unit.surcharge)} per side, per ball`,
        q.lines.surcharge,
      ]);
      rows.push([
        `Setup: ${q.designs === 2 ? 'two designs' : 'one design'}`,
        'one-off, not per ball',
        q.lines.setup,
      ]);
    }
    if (q.lines.pump) rows.push(['Air pump', 'one per order', q.lines.pump]);

    this.el.priceLines.innerHTML = rows
      .map(
        ([label, note, value]) =>
          `<li><span class="sb-cfg__price-label">${label}<small>${note}</small></span>` +
          `<span class="sb-cfg__price-value">${money(value)}</span></li>`
      )
      .join('');

    this.el.total.textContent = money(q.total);

    // Nudge toward the next tier only when there is one to reach.
    if (this.el.tierNote) {
      const next = q.nextTier;
      if (next) {
        const more = next.min - q.qty;
        this.el.tierNote.textContent =
          `Add ${more} more ${more === 1 ? 'ball' : 'balls'} to reach ${next.min}+ and save ${Math.round(next.discount * 100)}% on the ball price.`;
        this.el.tierNote.hidden = false;
      } else if (q.tier.discount) {
        this.el.tierNote.textContent =
          `${Math.round(q.tier.discount * 100)}% volume discount applied to the ball price.`;
        this.el.tierNote.hidden = false;
      } else {
        this.el.tierNote.hidden = true;
      }
    }

    if (this.el.pumpPrice) this.el.pumpPrice.textContent = `+${money(q.unit.pump)}`;
  }

  /*
   * One-line price summary in plain text, for surfaces that cannot take the
   * itemised list — the spec sheet, and the body of the fallback enquiry.
   * Built from the quote rather than scraped out of the DOM, so it stays
   * correct if the panel's markup changes.
   */
  priceSummaryText() {
    const q = this.currentQuote();
    if (!q) return '';
    const money = (v) => formatMoney(v, q.currency);
    const parts = [
      `${money(q.unit.ball)} per ball × ${q.qty}`,
      `${money(q.unit.surcharge)} print per side`,
      `${money(q.unit.setup)} setup${q.designs === 2 ? ' × 2 designs' : ''}`,
    ];
    if (q.lines.pump) parts.push(`${money(q.unit.pump)} pump`);
    if (q.tier.discount) parts.push(`${Math.round(q.tier.discount * 100)}% volume discount`);
    return `${parts.join(' · ')}. Goods only, excluding delivery and tax.`;
  }

  /*
   * Everything a lead needs to be actionable, in one object. The order form,
   * the lead email and the fallback enquiry all read from this, so there is one
   * definition of "the specification" rather than three that drift.
   */
  orderSpec() {
    const q = this.currentQuote();
    const fx = window.SB3D.fixingSummary ? window.SB3D.fixingSummary() : null;
    return {
      size: this.state.size,
      colour: this.colourText(),
      /*
       * Raw values as well as the display text. The CRM's colour and size
       * pick-lists take exact option values, and "Silver / Gold (alternating
       * panels)" is not one of them — sending it would fail the whole record.
       * The display text stays for the summary a human reads.
       */
      sizeCm: this.sizeCm(),
      colourPrimary: this.state.colour,
      colourSecond: this.state.twoTone ? this.state.colour2 : null,
      twoTone: !!this.state.twoTone,
      fixingType: this.state.fixingType,
      fixingPole: this.state.fixingPole,
      fixingCount: fx ? fx.count : null,
      displayMode: this.state.display,
      backMode: this.state.backMode,
      finish: this.finishText(),
      qty: this.state.qty,
      sides:
        this.state.sides === 'two'
          ? 'Front and back'
          : this.state.sides === 'none'
            ? 'No print'
            : 'One side',
      printed: this.state.sides !== 'none',
      designs: this.designCount(),
      fixings: this.fixingText(),
      display: { ground: 'Ground, valve down', hanging: 'Hanging, valve up' }[this.state.display],
      pump: this.state.pump,
      currency: this.state.currency,
      artwork: this.state.artwork ? this.state.artwork.file.name : null,
      backDesign:
        this.state.sides !== 'two'
          ? null
          : this.state.backMode === 'different'
            ? this.state.backArtwork
              ? this.state.backArtwork.file.name
              : 'Different design, file to follow'
            : 'Same design as the front',
      artworkWidth: this.state.artwork ? `${this.formatCm(this.artWidthCm())} wide` : null,
      quote: q,
      priceSummary: this.priceSummaryText(),
      total: q ? formatMoney(q.total, q.currency) : '',
    };
  }

  /*
   * Finish is not a choice — it follows from the colour. Seventeen of the
   * nineteen are mirror; iridescent and holographic are their own thing, and
   * printing "Mirror" over an iridescent ball on the spec sheet would be wrong.
   */
  finishText() {
    const c = (this.state.colour || '').toLowerCase();
    if (c === 'iridescent') return 'Iridescent';
    if (c.includes('holographic')) return 'Holographic';
    return 'Mirror';
  }

  /*
   * Opens the order form. The modal itself is the next step of the build; until
   * it lands this routes to the fallback enquiry path, which is not throwaway
   * scaffolding — it is the same route the form uses when the lead service is
   * unreachable, so a buyer never hits a dead end and a lead is never silently
   * lost.
   */
  startOrder() {
    /*
     * The fallback is not a placeholder for the modal — it is what runs when
     * the dialog cannot be built at all (very old browser, markup missing), and
     * again inside the form when the lead service is unreachable.
     */
    if (this.orderForm && this.orderForm.dlg) this.orderForm.open();
    else this.openFallbackEnquiry();
  }

  openFallbackEnquiry() {
    const spec = this.orderSpec();
    const lines = [
      `Size: ${spec.size}`,
      `Colour: ${spec.colour}`,
      `Finish: ${spec.finish}`,
      `Quantity: ${spec.qty}`,
      `Print: ${spec.sides}`,
      `Fixings: ${spec.fixings}`,
      `Air pump: ${spec.pump ? 'Yes' : 'No'}`,
      `Artwork: ${spec.artwork || 'Not yet supplied'}`,
      `Goods total (${spec.currency}): ${spec.total}`,
      spec.priceSummary,
    ].join('\n');

    const url = new URL(this.data.quoteUrl || '/pages/contact', window.location.origin);
    // Spec travels in the fragment, not the query string: it is not personal
    // data, but it does not belong in server logs or analytics either.
    url.hash = `sb-spec=${encodeURIComponent(lines)}`;
    window.location.assign(url.toString());
  }

  /* ------------------------------------------------------------ export */

  /*
   * Still resolves to a canvas, which is the whole point — the order form and
   * the lead email both need a rendered image of the configured ball.
   *
   * The Download mockup button that used to share this path is gone (Zac, 27
   * Aug), and so is the cart line it fed. This is NOT dead code: it produces
   * the render that travels with the lead, and the spec sheet renders through
   * SB3D directly.
   */
  /*
   * Export resolution follows the renderer's quality tier. On a phone this is
   * 1200 rather than 1600 — the cart preview is a data-URI PNG attached to a
   * line item, so at 1600 it is about 1MB per add-to-basket over a mobile
   * connection, and the tier already decided what this device can afford.
   */
  exportSize() {
    const q = window.SB3D.quality ? window.SB3D.quality() : null;
    return (q && q.exportSize) || EXPORT_SIZE;
  }

  async renderExportCanvas() {
    return window.SB3D.renderExport(this.exportSize());
  }

  /*
   * Open the A4 technical sheet. Deliberately available before artwork is
   * attached — the sheet is what a customer sends round for sign-off, and size,
   * colour, fixings and price are all decided before the logo arrives.
   *
   * Everything here is read from state that already exists. In particular the
   * price strings are lifted verbatim from the rendered elements rather than
   * recomputed, so the sheet cannot disagree with the page: the page's total is
   * the sum of the real cart lines, and this is that same string.
   */
  async openSpecSheet() {
    if (!window.SBSpecSheet) return;

    /*
     * Never touch textContent here. The button's whole content is an inline
     * <svg>, and the textContent setter replaces every child with a single text
     * node — an earlier save-and-restore of the label silently deleted the icon
     * on the first click and left a blank square for the rest of the session.
     * aria-busy carries the state instead; the CSS dims it.
     */
    const btn = this.el.spec;
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }

    try {
      const variant = this.variantFor(this.state.size, this.state.colour);
      const opened = await window.SBSpecSheet.open({
        product: this.data.product.title,
        size: this.state.size,
        colour: this.colourText(),
        finishNote: this.state.twoTone && this.state.colour2 ? 'Alternating panels' : '',
        sides: this.state.sides === 'two' ? 'Front and back' : 'One side',
        display: { ground: 'Ground, valve down', hanging: 'Hanging, valve up' }[this.state.display],
        fixings: this.fixingText(),
        artworkName: this.state.artwork ? this.state.artwork.file.name : 'Not yet supplied',
        /*
         * Says so on the sheet, because the sheet is what production works
         * from and a .png filename alone is easy to miss.
         */
        artworkFormat: this.state.artwork
          ? this.state.artwork.needsVectorising
            ? 'Raster, to be redrawn as vector before printing'
            : 'Vector, print ready'
          : '',
        artworkSize: this.state.artwork ? `${this.formatCm(this.artWidthCm())} wide` : '',
        artworkRotation: this.state.artwork ? `${this.state.rotationDeg}°` : '',
        qty: String(this.state.qty),
        priceLine: this.priceSummaryText(),
        total: this.el.total.textContent,
        /*
         * Not passed: the rendered price line already ends with the tier text,
         * so handing it over separately printed it twice, once inside the
         * breakdown and again under the total.
         */
        available: !!(variant && variant.available),
        exportSize: SPEC_SHEET_SIZE,
      });
      if (!opened) this.showSpecError();
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      }
    }
  }

  /* Popup blockers are the only realistic failure here, and the fix is the
   * customer's to make, so say what happened rather than failing silently. */
  showSpecError() {
    this.el.atcError.textContent = SPEC_BLOCKED_MSG;
    this.el.atcError.hidden = false;
  }
}

for (const root of document.querySelectorAll('.sb-cfg')) {
  new SBConfigurator(root);
}
